import DefaultLayout from 'admin/components/DefaultLayout';
import {
  BusinessInformationWrapper,
  StyledBusinessInformation,
  StyledBusinessInformationFooter,
  StyledBusinessInformationFooterMobile,
} from './style';
import { useIntl } from 'react-intl';
import { Button, Input, Modal, Spinner, Tabs } from '../../../ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BaseInformation } from '../../components/EditFarm/BaseInformation/BaseInformation';
import { useForm } from 'antd/lib/form/Form';
import { Form, message, UploadFile } from 'antd';
import { ContactInformation } from '../../components/EditFarm/ContactInformation/ContactInformation';
import { AdditionalInformation } from '../../components/EditFarm/AdditionalInformation/AdditionalInformation';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useAuth } from '../../../hooks/useAuth';
import { BusinessGallery } from '../../components/EditFarm/BusinessGallery/BusinessGallery';
import { BusinessAddresses } from '../../components/EditFarm/BusinessStructuralUnit/BusinessAddresses';
import {
  FarmsProps,
  ImageProps,
  InfrastructureProps,
} from '../../../interfaces/BackendModels';
import { RcFile } from 'antd/es/upload';
import { toSocialLinkValues } from 'types/SocialNetwork';
import { permissions } from '../../../config/config';
import {
  FIELD_META,
  ValidationError,
  ValidationReport,
  mergeReports,
} from './validation';

export const BusinessInformation = () => {
  const { user, can, refetchUser } = useAuth();
  const canSave = !!can(permissions.edit_company);
  const isGlobalAdmin = !!user?.isAdmin;

  const [fileLogo, setFileLogo] = useState<UploadFile[]>([]);
  const [fileGallery, setFileGallery] = useState<UploadFile[]>([]);
  const [isFooterHidden, setIsFooterHidden] = useState<boolean>(!canSave);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPvdModalOpen, setIsPvdModalOpen] = useState<boolean>(false);
  const [isPublicAllowed, setIsPublicAllowed] = useState<boolean>(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [pendingPublish, setPendingPublish] = useState<boolean>(false);
  const [tabValidationErrors, setTabValidationErrors] = useState<
    Record<string, number>
  >({});
  const [infrastructures, setInfrastructures] = useState<InfrastructureProps[]>(
    []
  );
  const [addressesReload, setAddressesReload] = useState<number>(0);

  const intl = useIntl();
  const [form] = useForm();
  const isPublicProfile = Form.useWatch('public', form);
  const pvdNr: string | undefined = Form.useWatch('client_pvd_nr', form);

  const stableFarmId = useMemo(() => {
    return user?.farmId;
  }, [user?.farmId]);

  const autoSyncTriggered = useRef(false);
  const pendingAutoSaveAfterRefetch = useRef(false);
  const pendingGalleryAutoSave = useRef(false);

  const { isLoading: farmLoading, refetch: refetchFarm } = useQueryApiClient({
    request: {
      url: `/api/v2/farms/${stableFarmId}`,
    },
    onSuccess: (response: FarmsProps) => handleSuccessFarmDataFetch(response),
  });

  const { refetch: refetchInfrastructures } = useQueryApiClient({
    request: {
      url: '/api/v2/farm/infrastructures',
      data: {
        page: 1,
        page_size: 999,
        filter: { farmId: stableFarmId },
      },
    },
    enabled: !!stableFarmId,
    onSuccess: (response: { data?: InfrastructureProps[] }) => {
      setInfrastructures(response?.data ?? []);
    },
  });

  const handleInfrastructureChanged = () => {
    pendingAutoSaveAfterRefetch.current = true;
    refetchInfrastructures();
  };

  const handleUserLogoChange: React.Dispatch<
    React.SetStateAction<UploadFile[]>
  > = (value) => {
    pendingGalleryAutoSave.current = true;
    setFileLogo(value);
  };

  const handleUserGalleryChange: React.Dispatch<
    React.SetStateAction<UploadFile[]>
  > = (value) => {
    pendingGalleryAutoSave.current = true;
    setFileGallery(value);
  };

  useEffect(() => {
    if (!pendingAutoSaveAfterRefetch.current) return;
    pendingAutoSaveAfterRefetch.current = false;
    handleSubmit();
  }, [infrastructures]);

  useEffect(() => {
    if (!pendingGalleryAutoSave.current) return;
    pendingGalleryAutoSave.current = false;
    handleSubmit();
  }, [fileLogo, fileGallery]);

  useEffect(() => {
    if (!pendingPublish || !isPublicProfile) return;
    setPendingPublish(false);
    handleSubmit({ publishAttempt: true });
  }, [pendingPublish, isPublicProfile]);

  useEffect(() => {
    if (pendingPublish || !form.isFieldTouched('public')) return;
    form.setFields(
      form
        .getFieldsError()
        .filter((f) => f.errors.length)
        .map((f) => ({ name: f.name, errors: [] }))
    );
    form
      .validateFields()
      .then(() => throwErrorMessages(undefined, { silent: true }))
      .catch((errorInfo) =>
        throwErrorMessages(errorInfo as ValidationError, { silent: true })
      );
  }, [isPublicProfile]);

  const { appendData: submitData, isLoading: isBusinessInformationSaving } =
    useQueryApiClient({
      request: {
        url: '/api/v2/farms/:farmId',
        method: 'POST',
        multipart: true,
      },
      onSuccess: (response: FarmsProps) => {
        form.setFieldValue('location_issue', response?.location_issue ?? null);
        message.success({
          content: intl.formatMessage({ id: 'message.success_update_farm' }),
          key: 'farm-save-status',
        });
        refetchUser();
        refetchInfrastructures();
        setAddressesReload((prev) => prev + 1);
      },
      onError: (error) => {
        form.setFields(
          Object.entries(error?.errors ?? {}).map(([name, errors]) => ({
            name,
            errors: errors as string[],
          }))
        );
        message.error({
          content: intl.formatMessage({ id: 'message.error_farm_edit' }),
          key: 'farm-save-status',
        });
      },
    });

  const { appendData: fetchFarmByPvdNumber, isLoading: isFarmFetching } =
    useQueryApiClient({
      request: {
        url: '/api/v2/llkc/farmSyncByPvdNumber/:farmId',
        disableOnMount: true,
      },
      onSuccess: () => {
        refetchFarm();
      },
      onError: (error) => {
        if (error.code === 400)
          message.error(
            intl.formatMessage({ id: 'message.pvd_registry_contact_support' })
          );
      },
    });

  const { appendData: syncFarm, isLoading: isFarmSyncing } = useQueryApiClient({
    request: {
      url: '/api/v2/farms/:farmId/sync',
      method: 'POST',
      errorWithHeaders: true,
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'message.success_farm_sync' }));
      refetchFarm();
    },
    onError: (errorResponse) => {
      if (errorResponse?.status === 429) {
        message.warning(intl.formatMessage({ id: 'message.please_wait_time' }));
        return;
      }

      message.error(intl.formatMessage({ id: 'message.error_farm_edit' }));
    },
  });

  const { appendData: autoSyncFarm } = useQueryApiClient({
    request: {
      url: '/api/v2/farms/:farmId/sync',
      method: 'POST',
      disableOnMount: true,
    },
    onSuccess: () => {
      refetchFarm();
    },
  });

  const { appendData: farmsLogoFetch, isLoading: isLogoLoading } =
    useQueryApiClient({
      request: {
        url: '/api/v2/image',
        multipart: true,
        disableOnMount: true,
      },
      onSuccess: (response, passOnSuccess) =>
        handleImageFile(response, passOnSuccess.logoUrl, true),
    });

  const { appendData: farmsGalleryFetch, isLoading: isGalleryLoading } =
    useQueryApiClient({
      request: {
        url: '/api/v2/image',
        multipart: true,
        disableOnMount: true,
      },
      onSuccess: (response, passOnSuccess) =>
        handleImageFile(response, passOnSuccess.galleryUrl, false),
    });

  const handleImageFile = (response: any, url: string, isLogo: boolean) => {
    const fileName = url.split('/').pop() || 'unknown_file';

    const file = new File([response], fileName, { type: response.type });

    const rcFile = file as RcFile;

    const blobUrl = URL.createObjectURL(response);

    const fileObj: UploadFile = {
      uid: rcFile.uid,
      name: fileName,
      preview: blobUrl,
      originFileObj: rcFile,
    };

    if (isLogo) {
      setFileLogo([fileObj]);
    } else {
      setFileGallery((prev) => [...prev, fileObj]);
    }
  };

  function triggerAutoSyncIfStale(syncAt: string | null): boolean {
    const isToday =
      !!syncAt && new Date(syncAt).toDateString() === new Date().toDateString();
    if (isToday || autoSyncTriggered.current || !stableFarmId) {
      return false;
    }
    autoSyncTriggered.current = true;
    autoSyncFarm({}, { farmId: stableFarmId });
    return true;
  }

  function handleSuccessFarmDataFetch(response: FarmsProps) {
    const keysToMapToIds = new Set(['types', 'services', 'quality_marks']);
    const galleryUrls = response.images.map(
      (img: ImageProps) => img.url_original
    );
    const publicAllowed = !!response.public_allowed;

    setIsPublicAllowed(publicAllowed);

    if (response.logo) {
      farmsLogoFetch({ path: response.logo }, [], { logoUrl: response.logo });
    }
    if (galleryUrls) {
      galleryUrls.forEach((url: string) =>
        farmsGalleryFetch({ path: url }, [], { galleryUrl: url })
      );
    }

    Object.entries(response).forEach(([key, value]) => {
      if (key === 'public') {
        form.setFieldValue(key, publicAllowed ? value : false);
        return;
      }

      if (key === 'social_networks') {
        form.setFieldValue('social_links', toSocialLinkValues(value));
        return;
      }

      const finalValue = keysToMapToIds.has(key)
        ? value.map((item: { id: number }) => item.id)
        : value;
      form.setFieldValue(key, finalValue);
    });

    triggerAutoSyncIfStale(response.sync_at);
  }

  const handleOpenAddPvdModal = () => {
    setIsPvdModalOpen(true);
  };

  const items = [
    {
      key: 'base_information',
      label: intl.formatMessage({ id: 'navigation.company' }),
      children: (
        <BaseInformation
          onAddPvdNumberClick={handleOpenAddPvdModal}
          publicAllowed={isPublicAllowed}
        />
      ),
    },
    {
      key: 'contact_information',
      label: intl.formatMessage({ id: 'navigation.contact_information' }),
      children: <ContactInformation />,
    },
    {
      key: 'additional_information',
      label: intl.formatMessage({ id: 'navigation.additional_information' }),
      children: <AdditionalInformation />,
    },
    {
      key: 'business_gallery',
      label: intl.formatMessage({ id: 'navigation.gallery' }),
      children: (
        <BusinessGallery
          setFileLogo={handleUserLogoChange}
          setFileGallery={handleUserGalleryChange}
          fileGallery={fileGallery}
          fileLogo={fileLogo}
        />
      ),
    },
    {
      key: 'business_addresses',
      label: intl.formatMessage({ id: 'navigation.business_addresses' }),
      children: (
        <BusinessAddresses
          reload={addressesReload}
          onInfrastructureChanged={handleInfrastructureChanged}
        />
      ),
    },
  ];

  const buildFieldErrorReport = (
    errorInfo?: ValidationError
  ): ValidationReport => {
    const lines: string[] = [];
    const counts: Record<string, number> = {};

    (errorInfo?.errorFields ?? []).forEach((f) => {
      const fieldName = String(f.name?.[0] ?? '');
      const meta = FIELD_META[fieldName];
      if (!meta) return;

      const tabItem = items.find((i) => i.key === meta.tabKey);
      const tabLabel =
        typeof tabItem?.label === 'string' ? tabItem.label : meta.tabKey;
      const fieldLabel = intl.formatMessage({ id: meta.labelKey });
      const errMsg = f.errors?.[0] ?? '';

      lines.push(`${tabLabel} — ${fieldLabel}: ${errMsg}`);
      counts[meta.tabKey] = (counts[meta.tabKey] || 0) + 1;
    });

    return { lines, counts };
  };

  const buildPublicProfileReport = (): ValidationReport => {
    const lines: string[] = [];
    const counts: Record<string, number> = {};

    if (!isPublicProfile) return { lines, counts };

    if (!fileLogo[0]) {
      lines.push(intl.formatMessage({ id: 'validation.logo_required' }));
      counts.business_gallery = (counts.business_gallery || 0) + 1;
    }
    if (!fileGallery.length) {
      lines.push(intl.formatMessage({ id: 'validation.gallery_required' }));
      counts.business_gallery = (counts.business_gallery || 0) + 1;
    }

    const hasPublicMapInfra = infrastructures.some(
      (i) =>
        i.status === true &&
        i.show_on_map === true &&
        !!i.maps_api_point?.coordinates?.length
    );
    const addressPrefix = form.getFieldValue(
      'registered_address_is_actual_address'
    )
      ? 'registered'
      : 'actual';
    const addressBecomesUnit =
      form.getFieldValue('company_type') === null &&
      form.getFieldValue(`${addressPrefix}_lks_lat`) != null &&
      form.getFieldValue(`${addressPrefix}_lks_long`) != null;
    if (!hasPublicMapInfra && !addressBecomesUnit) {
      lines.push(
        intl.formatMessage({ id: 'validation.infrastructures_show_on_map' })
      );
      counts.business_addresses = (counts.business_addresses || 0) + 1;
    }

    return { lines, counts };
  };

  const renderErrorList = (lines: string[], headerId: string) => (
    <div style={{ textAlign: 'left', display: 'inline-block' }}>
      <div style={{ marginBottom: 4 }}>
        {intl.formatMessage({ id: headerId })}:
      </div>
      {lines.map((line, i) => (
        <div key={i}>• {line}</div>
      ))}
    </div>
  );

  interface SubmitOptions {
    silent?: boolean;
    publishAttempt?: boolean;
  }

  const throwErrorMessages = (
    errorInfo?: ValidationError,
    options: SubmitOptions = {}
  ) => {
    const { lines, counts } = mergeReports(
      buildFieldErrorReport(errorInfo),
      buildPublicProfileReport()
    );

    setTabValidationErrors(counts);

    if (lines.length) {
      if (!options.silent) {
        message.error({
          content: renderErrorList(
            lines,
            options.publishAttempt
              ? 'company.publish_fill_all_info'
              : 'validation.fix_errors'
          ),
          duration: 6,
          key: 'validation-errors',
        });
      }
      return false;
    }
    message.destroy('validation-errors');
    return true;
  };

  const changeStep = async (desiredStep: string) => {
    const index = items.findIndex((step) => step.key === desiredStep);

    if (index === -1) {
      return;
    }

    if (canSave) {
      if (['contact_information', 'business_addresses'].includes(desiredStep)) {
        setIsFooterHidden(true);
      } else {
        setIsFooterHidden(false);
      }

      await handleSubmit({ silent: true });
    }

    setCurrentStep(index);
  };

  const handleSubmit = async (options: SubmitOptions = {}) => {
    try {
      await form.validateFields();

      if (!throwErrorMessages(undefined, options)) {
        return;
      }

      const formData = new FormData();
      const values = form.getFieldsValue();

      const rcFile = fileLogo[0]?.originFileObj;

      if (rcFile && rcFile instanceof Blob) {
        const realFile = new File([rcFile], rcFile.name, {
          type: rcFile.type,
          lastModified: rcFile.lastModified || Date.now(),
        });

        formData.append('logo_img', realFile);
      }

      if (fileGallery.length) {
        fileGallery.forEach((img: UploadFile) => {
          const galleryFile = img.originFileObj;

          if (galleryFile instanceof Blob) {
            const realFile = new File([galleryFile], galleryFile.name, {
              type: galleryFile.type,
              lastModified: galleryFile.lastModified || Date.now(),
            });

            formData.append('gallery[]', realFile, realFile.name);
          }
        });
      }

      Object.entries(values).forEach(([key, value]) => {
        if (
          key === 'gallery' ||
          key === 'logo' ||
          key === 'images' ||
          key === 'public_allowed'
        ) {
          return;
        }

        if (Array.isArray(value)) {
          if (key === 'quality_marks') {
            return;
          }
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
        } else if (key === 'social_links') {
          formData.append(key, JSON.stringify(value ?? {}));
        } else if (value != null) {
          formData.append(key, String(value));
        }
      });

      submitData(formData, { farmId: stableFarmId });
    } catch (errorInfo) {
      throwErrorMessages(errorInfo as ValidationError, options);
    }
  };

  const handleSaveClick = () => {
    if (
      isPublicAllowed &&
      !form.getFieldValue('public') &&
      !form.isFieldTouched('public')
    ) {
      setIsPublishModalOpen(true);
      return;
    }
    handleSubmit();
  };

  const handlePublishDecline = () => {
    setIsPublishModalOpen(false);
    handleSubmit();
  };

  const handlePublishConfirm = () => {
    setIsPublishModalOpen(false);
    form.setFieldValue('public', true);
    setPendingPublish(true);
  };

  const handlePvdModalSave = () => {
    if (!stableFarmId) {
      return;
    }

    fetchFarmByPvdNumber({ pvd_number: pvdNr }, { farmId: stableFarmId });
  };

  const handleSyncFarm = () => {
    syncFarm({}, { farmId: stableFarmId });
  };

  const Footer = () => {
    if (isFooterHidden) {
      return null;
    }
    return (
      <>
        <StyledBusinessInformationFooter>
          <div className={'footer'}>
            <Button
              label={intl.formatMessage({ id: 'general.save' })}
              onClick={handleSaveClick}
              type={'primary'}
            />
          </div>
        </StyledBusinessInformationFooter>
        <StyledBusinessInformationFooterMobile>
          <div className={'footer'}>
            <Button
              label={intl.formatMessage({ id: 'general.save' })}
              onClick={handleSaveClick}
              type={'primary'}
            />
          </div>
        </StyledBusinessInformationFooterMobile>
      </>
    );
  };

  return (
    <BusinessInformationWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
          },
          {
            title: intl.formatMessage({ id: 'navigation.company' }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <StyledBusinessInformation>
            <Spinner
              spinning={
                farmLoading ||
                isBusinessInformationSaving ||
                isLogoLoading ||
                isGalleryLoading
              }
            >
              <Form form={form} layout={'vertical'}>
                <Tabs
                  items={items}
                  tabValidationErrors={tabValidationErrors}
                  onChange={(desiredStep) => {
                    changeStep(desiredStep);
                  }}
                  activeKey={items[currentStep].key}
                  tabBarExtraContent={
                    isGlobalAdmin && (
                      <Button
                        label={intl.formatMessage({
                          id: 'general.refresh_data',
                        })}
                        type={'primary'}
                        onClick={handleSyncFarm}
                        loading={isFarmSyncing}
                      />
                    )
                  }
                />
                <Footer />
                <Modal
                  open={isPvdModalOpen}
                  title={intl.formatMessage({ id: 'company.pvd_number' })}
                  footer={
                    <Button
                      type={'primary'}
                      label={intl.formatMessage({ id: 'general.save' })}
                      onClick={handlePvdModalSave}
                      loading={isFarmFetching}
                    />
                  }
                  onCancel={() => setIsPvdModalOpen(false)}
                >
                  <Input
                    layout={'vertical'}
                    name={'client_pvd_nr'}
                    validations={['pvdNumber']}
                  />
                </Modal>
                <Modal
                  open={isPublishModalOpen}
                  title={intl.formatMessage({
                    id: 'company.publish_confirm_title',
                  })}
                  onCancel={() => setIsPublishModalOpen(false)}
                  footer={
                    <>
                      <Button
                        label={intl.formatMessage({ id: 'general.no' })}
                        onClick={handlePublishDecline}
                      />
                      <Button
                        type={'primary'}
                        label={intl.formatMessage({ id: 'general.yes' })}
                        onClick={handlePublishConfirm}
                      />
                    </>
                  }
                >
                  {intl.formatMessage({ id: 'company.publish_confirm_text' })}
                </Modal>
              </Form>
            </Spinner>
          </StyledBusinessInformation>
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
      <DefaultLayout.PageFooter>
        <Footer />
      </DefaultLayout.PageFooter>
    </BusinessInformationWrapper>
  );
};
